import React, { useCallback, useMemo, useState } from 'react';
import { AddAttachmentsBrushaway, Button, ConfirmDialog, PhotoCarousel, Table, TableSize } from '@ui';
import { SectionHeader } from '@ui/molecules/Header/SectionHeader';
import { translate } from '@utils/i18n';
import { AttachmentsEmptyState } from './components/AttachmentsEmptyState';
import { useStableCellRenderers } from './hooks/useStableCellRenderers';
import { AttachmentsFilterBar } from './components/AttachmentsFilterBar';
import { AttachmentsToolbar } from './components/AttachmentsToolbar';
import { useAttachmentsQuery } from './hooks/useAttachmentsQuery';
import { useAttachmentPreview } from './hooks/useAttachmentPreview';
import { useAttachmentDeletion } from './hooks/useAttachmentDeletion';
import { useAttachmentsUpload } from './hooks/useAttachmentsUpload';
import { useUploadProgressToast } from './hooks/useUploadProgressToast';
import { filterRows } from './utils/filtering';
import { buildBaseColumns, BASE_SORTABLE_COLUMNS } from './utils/buildBaseColumns';
import './AttachmentsSurfaceStyles.scss';
/**
 * Fallback only, used when the server did not supply `maxUploadSizeBytes` (e.g. a story or a test
 * rendering the surface directly). The real cap is per-tenant and server-owned — presign rejects
 * anything above it with 422 regardless of what this client believes, so the two must agree or
 * users get a late failure instead of an early one.
 */
const DEFAULT_MAX_UPLOAD_SIZE_BYTES = 150 * 1024 * 1024;
// Client-side pagination: the tab already loads the owner's whole attachment list in one request,
// so paging here is presentation only — no refetch, and the filter/sort/selection state all keep
// operating over the full filtered set.
const DEFAULT_PAGE_SIZE = 25;
// Newest first. `uploadedSortValue` is `created_at` as a number, so a freshly added attachment
// lands at the top of the first page without the user touching the sort.
const DEFAULT_SORT_KEY = 'uploadedSortValue';
const DEFAULT_SORT_DIRECTION = 'desc';
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
/**
 * The attachments listing surface, shared by every attachment screen: data fetching, client-side
 * search/filter/sort, selection, preview, row and bulk delete, and the Add-attachments drawer.
 *
 * It branches on no entity: columns, filters, bulk actions and drawer fields all arrive already
 * built from `config`, so adding a screen means writing a config and a mount — never editing this
 * file. See `docs/ai-knowledge/architecture/direct-uploads-shared-layer.md`.
 *
 * Owns `useAttachmentsUpload` (which in turn owns `useDirectUpload`) — this is "the Page" in
 * `AddAttachmentsBrushaway`'s own module doc: the organism never touches the upload queue
 * directly, so the queue survives the drawer closing. `useUploadProgressToast` reacts to that same
 * hoisted state to drive the background progress toast and navigate-away warning; it never touches
 * the queue either.
 */
export function AttachmentsSurface({ config, ownerId, maxUploadSizeBytes = DEFAULT_MAX_UPLOAD_SIZE_BYTES, onAddAttachment, readOnly = false, title, canAdd = true }) {
    var _a, _b, _c, _d, _e, _f;
    const { entityKey, namespace } = config;
    const t = useCallback((key, options) => translate(key, Object.assign({ namespace }, options)), [namespace]);
    const { data, isLoading, isError } = useAttachmentsQuery(entityKey, ownerId, config.data.fetchAll);
    const apiRows = useMemo(() => data !== null && data !== void 0 ? data : [], [data]);
    const rows = useMemo(() => apiRows.map((row) => config.data.toRow(row, t)), [apiRows, config.data, t]);
    const transport = useMemo(() => config.upload.transport(ownerId), [config.upload, ownerId]);
    const upload = useAttachmentsUpload({
        entityKey,
        ownerId,
        transport,
        defaultAttributes: config.upload.defaultAttributes,
        toFields: config.upload.toFields,
        t,
        successToast: config.upload.successToast
    });
    const { openDrawer, updateItemMetadata } = upload;
    useUploadProgressToast({
        entityKey,
        ownerId,
        namespace: config.progressToastNamespace,
        isOpen: upload.isOpen,
        items: upload.items,
        isUploading: upload.isUploading,
        aggregateProgress: upload.aggregateProgress,
        openDrawer
    });
    const handleAddAttachmentClick = useCallback(() => {
        openDrawer();
        onAddAttachment === null || onAddAttachment === void 0 ? void 0 : onAddAttachment();
    }, [openDrawer, onAddAttachment]);
    const [search, setSearch] = useState('');
    const [selections, setSelections] = useState({});
    const [selectedIds, setSelectedIds] = useState([]);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [sortBy, setSortBy] = useState(DEFAULT_SORT_KEY);
    const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION);
    const clearSelection = useCallback(() => setSelectedIds([]), []);
    const columnContext = useMemo(() => ({ t, ownerId, rows, readOnly }), [t, ownerId, rows, readOnly]);
    const filters = useMemo(() => { var _a, _b; return (_b = (_a = config.filters) === null || _a === void 0 ? void 0 : _a.call(config, columnContext)) !== null && _b !== void 0 ? _b : []; }, [config, columnContext]);
    const { searchableText } = config;
    // E2E-25: changing any filter or the search clears the selection.
    const handleSearchChange = useCallback((value) => {
        setSearch(value);
        clearSelection();
    }, [clearSelection]);
    const handleFilterChange = useCallback((key, values) => {
        setSelections((prev) => (Object.assign(Object.assign({}, prev), { [key]: values })));
        clearSelection();
    }, [clearSelection]);
    const filteredRows = useMemo(() => filterRows(rows, search, searchableText !== null && searchableText !== void 0 ? searchableText : ((row) => [row.name]), filters, selections), [rows, search, searchableText, filters, selections]);
    // Every path that selects "all" must use this, not `filteredRows` -- otherwise a row the
    // surface refuses to select by checkbox still lands in the selection via Select all, and the
    // bulk Delete then targets a row whose id its endpoint cannot resolve.
    const selectableRows = useMemo(() => (config.isRowSelectable ? filteredRows.filter(config.isRowSelectable) : filteredRows), [filteredRows, config.isRowSelectable]);
    // Sorting is driven here rather than left to `Table`'s internal state, because `Table` starts
    // unsorted and offers no way to seed a default. Supplying `onSort` puts it in delegated mode, so
    // the header indicator always matches the order actually applied.
    const sortedRows = useMemo(() => {
        if (!sortBy || !sortDirection)
            return filteredRows;
        const factor = sortDirection === 'asc' ? 1 : -1;
        return [...filteredRows].sort((a, b) => {
            const aVal = a[sortBy];
            const bVal = b[sortBy];
            if (aVal == null && bVal == null)
                return 0;
            if (aVal == null)
                return factor;
            if (bVal == null)
                return -factor;
            if (typeof aVal === 'number' && typeof bVal === 'number')
                return (aVal - bVal) * factor;
            return String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base' }) * factor;
        });
    }, [filteredRows, sortBy, sortDirection]);
    const handleSort = useCallback((dataKey, direction) => {
        setSortBy(direction ? dataKey : null);
        setSortDirection(direction);
    }, []);
    const filterOptions = useMemo(() => Object.fromEntries(filters.map((filter) => [filter.key, filter.options(rows)])), [filters, rows]);
    const handleSelectionChange = useCallback((id, checked) => {
        setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((existing) => existing !== id)));
    }, []);
    const handleSelectAll = useCallback((selectAll) => {
        setSelectedIds(selectAll ? selectableRows.map((row) => row.id) : []);
    }, [selectableRows]);
    const paginationLabels = useMemo(() => ({
        page: t('pagination.page'),
        of: t('pagination.of'),
        itemsPerPage: t('pagination.items_per_page'),
        pageInputAriaLabel: t('pagination.page'),
        pageSizeAriaLabel: t('pagination.page_size'),
        firstPage: t('pagination.first_page'),
        previousPage: t('pagination.previous_page'),
        nextPage: t('pagination.next_page'),
        lastPage: t('pagination.last_page')
    }), [t]);
    const preview = useAttachmentPreview(sortedRows);
    const deletion = useAttachmentDeletion({
        entityKey,
        ownerId,
        destroyOne: config.data.destroyOne,
        destroyMany: config.data.destroyMany,
        clearSelection,
        labels: {
            successToast: config.labels.deleteSuccessToast,
            errorToast: config.labels.deleteErrorToast,
            unlinkSuccessToast: (_a = config.labels.unlink) === null || _a === void 0 ? void 0 : _a.successToast,
            unlinkErrorToast: (_b = config.labels.unlink) === null || _b === void 0 ? void 0 : _b.errorToast
        }
    });
    const handleRequestRowDelete = useCallback((row) => { var _a, _b; return deletion.requestDelete([row.id], row.name, { unlink: (_b = (_a = config.isRowUnlink) === null || _a === void 0 ? void 0 : _a.call(config, row)) !== null && _b !== void 0 ? _b : false }); }, [deletion.requestDelete, config.isRowUnlink]);
    // Bound here rather than in the column builder so the surface's action cell gets the same
    // confirm dialog and invalidation the default delete icon uses.
    const rowActionRenderer = useMemo(() => config.renderRowAction
        ? (row) => {
            var _a, _b;
            return (_b = (_a = config.renderRowAction) === null || _a === void 0 ? void 0 : _a.call(config, row, {
                requestDelete: handleRequestRowDelete,
                invalidate: deletion.invalidate,
                isMutating: deletion.isMutating
            })) !== null && _b !== void 0 ? _b : null;
        }
        : undefined, [config.renderRowAction, handleRequestRowDelete, deletion.invalidate, deletion.isMutating]);
    const selectedRows = useMemo(() => filteredRows.filter((row) => selectedIds.includes(row.id)), [filteredRows, selectedIds]);
    const bulkActions = (_c = config.renderBulkActions) === null || _c === void 0 ? void 0 : _c.call(config, {
        selectedIds,
        selectedRows,
        rows,
        isMutating: deletion.isMutating,
        clearSelection,
        invalidate: deletion.invalidate
    });
    const entityColumns = useMemo(() => config.entityColumns(columnContext), [config, columnContext]);
    const columns = useMemo(() => buildBaseColumns({
        t,
        onPreviewClick: preview.handlePreviewClick,
        onRequestRowDelete: handleRequestRowDelete,
        renderNameCell: (row) => config.renderNameCell(row, { readOnly }),
        rowDeleteAriaLabel: config.labels.rowDeleteAriaLabel,
        entityColumns,
        renderRowAction: rowActionRenderer,
        hideRowAction: readOnly
    }), [
        t,
        preview.handlePreviewClick,
        handleRequestRowDelete,
        config.renderNameCell,
        config.labels.rowDeleteAriaLabel,
        entityColumns,
        rowActionRenderer,
        readOnly
    ]);
    const stableColumns = useStableCellRenderers(columns);
    const sortableColumns = useMemo(() => { var _a; return [...BASE_SORTABLE_COLUMNS, ...((_a = config.entitySortableColumns) !== null && _a !== void 0 ? _a : [])]; }, [config.entitySortableColumns]);
    const { pendingDelete } = deletion;
    // Falls back to a non-empty title even while the dialog is closed (`pendingDelete === null`) —
    // `ConfirmDialog` always renders the underlying `Modal`, which warns without an `ariaLabel`.
    let deleteDialogTitle = config.labels.deleteConfirmBulkTitle(0);
    const unlinkLabels = (pendingDelete === null || pendingDelete === void 0 ? void 0 : pendingDelete.unlink) ? config.labels.unlink : undefined;
    if ((pendingDelete === null || pendingDelete === void 0 ? void 0 : pendingDelete.fileName) && unlinkLabels) {
        deleteDialogTitle = unlinkLabels.confirmTitle(pendingDelete.fileName);
    }
    else if (pendingDelete === null || pendingDelete === void 0 ? void 0 : pendingDelete.fileName) {
        deleteDialogTitle = config.labels.deleteConfirmSingleTitle(pendingDelete.fileName);
    }
    else if (pendingDelete) {
        deleteDialogTitle = config.labels.deleteConfirmBulkTitle(pendingDelete.ids.length);
    }
    const isLoadingOrError = isLoading || isError;
    const isEmpty = !isLoadingOrError && rows.length === 0;
    const addButton = (React.createElement(Button, { variant: "default", onClick: handleAddAttachmentClick },
        React.createElement("i", { className: "fa-light fa-plus", "aria-hidden": "true" }),
        " ",
        t('add_button')));
    const uploadColumns = useMemo(() => config.upload.columns({ updateItemMetadata, rows }), [config.upload, updateItemMetadata, rows]);
    return (React.createElement("div", { className: `attachments-surface${config.className ? ` ${config.className}` : ''}` },
        React.createElement(SectionHeader, { title: title !== null && title !== void 0 ? title : t('title', { count: filteredRows.length }), actions: isEmpty || readOnly || !canAdd ? undefined : addButton }),
        isEmpty ? (React.createElement(AttachmentsEmptyState, { title: t('empty.title'), description: readOnly ? t('empty.read_only_description') : t('empty.description'), ctaLabel: readOnly || !canAdd ? undefined : t('empty.cta'), onAdd: readOnly || !canAdd ? undefined : handleAddAttachmentClick })) : (React.createElement(React.Fragment, null,
            React.createElement(AttachmentsFilterBar, { searchId: `${entityKey}-search`, search: search, onSearchChange: handleSearchChange, searchPlaceholder: t('search_placeholder'), filters: filters, options: filterOptions, selections: selections, onFilterChange: handleFilterChange }),
            !readOnly && (React.createElement(AttachmentsToolbar, { selectedCount: selectedIds.length, selectedLabel: t('toolbar.selected', { count: selectedIds.length }), selectAllLabel: t('toolbar.select_all', { count: selectableRows.length }), onSelectAll: () => setSelectedIds(selectableRows.map((row) => row.id)), clearSelectionLabel: t('toolbar.clear_selection'), onClearSelection: clearSelection, deleteLabel: t('toolbar.delete'), disabled: selectedIds.length === 0 || deletion.isMutating, onRequestDelete: () => deletion.requestDelete(selectedIds) }, bulkActions)),
            React.createElement(Table, { className: "attachments-surface__table", variant: "detail", size: TableSize.Full, columns: stableColumns, data: sortedRows, getItemId: (row) => row.id, selectable: !readOnly, isRowSelectable: config.isRowSelectable, selectedItems: selectedIds, onSelectionChange: handleSelectionChange, onSelectAll: handleSelectAll, sortable: true, sortableColumns: sortableColumns, sortBy: sortBy, sortDirection: sortDirection, onSort: handleSort, paginated: true, pageSize: pageSize, onPageSizeChange: setPageSize, pageSizeOptions: PAGE_SIZE_OPTIONS, paginationLabels: paginationLabels, isLoading: isLoading, isError: isError }))),
        React.createElement(PhotoCarousel, { open: preview.carouselOpen, index: preview.carouselIndex, slides: preview.carouselSlides, onClose: preview.closeCarousel, onIndexChange: preview.setCarouselIndex }),
        React.createElement(PhotoCarousel, { open: preview.pdfOpen, index: 0, slides: preview.pdfUrl ? [{ type: 'document', src: preview.pdfUrl, alt: t('preview.pdf_label') }] : [], onClose: preview.closePdf }),
        React.createElement(ConfirmDialog, { isOpen: pendingDelete !== null, title: deleteDialogTitle, description: (_d = unlinkLabels === null || unlinkLabels === void 0 ? void 0 : unlinkLabels.confirmDescription) !== null && _d !== void 0 ? _d : config.labels.deleteConfirmDescription, confirmLabel: (_e = unlinkLabels === null || unlinkLabels === void 0 ? void 0 : unlinkLabels.confirmLabel) !== null && _e !== void 0 ? _e : config.labels.deleteConfirmLabel, confirmVariant: "danger", cancelLabel: config.labels.deleteCancelLabel, loading: deletion.isMutating, onConfirm: deletion.confirmDelete, onCancel: deletion.cancelDelete }),
        React.createElement(AddAttachmentsBrushaway, { open: upload.isOpen, items: upload.items, isUploading: upload.isUploading, aggregateProgress: upload.aggregateProgress, onFilesSelected: upload.onFilesSelected, onNameChange: upload.onNameChange, onRemoveItem: upload.onRemoveItem, onRetryItem: upload.onRetryItem, onRetryFailed: upload.onRetryFailed, onCancelUpload: upload.onCancelUpload, onSubmit: upload.onSubmit, onClose: upload.onClose, onCloseToBackground: upload.onCloseToBackground, maxSizeBytes: maxUploadSizeBytes, accept: config.upload.accept, columns: uploadColumns, labels: config.upload.labels }), (_f = config.renderExtras) === null || _f === void 0 ? void 0 :
        _f.call(config, { ownerId })));
}
